/**
 * Unified API Service
 * 
 * This is the single interface your app components will use.
 * It automatically routes requests to the correct backend (Smart or SWS)
 * based on the user's system, so your components never need to know
 * which system they're talking to.
 */

import { User } from '../types';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { storageService } from './storageService';
import { configService } from './configService';
import * as Sentry from '@sentry/react';
import { trackApiCall, captureExceptionWithContext, addUserActionBreadcrumb, getSentryLogger } from '../utils/sentryHelpers';
// (2026-04-24) Per-feature API modules extracted from this file. Types are
// re-exported below so existing consumer imports keep working.
import type { ApiConfig } from './apiConfig';
import {
  lookupCheckoutTags as smartLookupCheckoutTags,
  submitManualCheckout as smartSubmitManualCheckout,
  getManualCheckoutHistory as smartGetManualCheckoutHistory,
} from './smart/manualCheckoutApi';
import type {
  ManualCheckoutTagInfo,
  ManualCheckoutPayload,
  ManualCheckoutResult,
  ManualCheckoutHistoryOptions,
  ManualCheckoutHistoryRow,
  ManualCheckoutHistoryResult,
} from './smart/manualCheckoutApi';
import {
  getDeliveryTicketList as smartGetDeliveryTicketList,
  getTransferTicketList as smartGetTransferTicketList,
  getSmartboxStatusList as smartGetSmartboxStatusList,
  getBaseRfidList as smartGetBaseRfidList,
} from './smart/listApis';
import type {
  DeliveryTicketFilters,
  DeliveryTicketRow,
  DeliveryTicketResult,
  TransferTicketFilters,
  TransferTicketRow,
  TransferTicketResult,
  SmartboxStatusFilters,
  SmartboxStatusRow,
  SmartboxStatusResult,
  BaseRfidFilters,
  BaseRfidRow,
  BaseRfidResult,
} from './smart/listApis';

// Re-export the extracted types so every page/hook that imports them from
// this file keeps working without a churned import path.
export type {
  ApiConfig,
  ManualCheckoutTagInfo,
  ManualCheckoutPayload,
  ManualCheckoutResult,
  ManualCheckoutHistoryOptions,
  ManualCheckoutHistoryRow,
  ManualCheckoutHistoryResult,
  DeliveryTicketFilters,
  DeliveryTicketRow,
  DeliveryTicketResult,
  TransferTicketFilters,
  TransferTicketRow,
  TransferTicketResult,
  SmartboxStatusFilters,
  SmartboxStatusRow,
  SmartboxStatusResult,
  BaseRfidFilters,
  BaseRfidRow,
  BaseRfidResult,
};

// Unified data models that work for both systems
export interface UnifiedTagInfo {
  id: string;
  rfidNumber: string;
  productId?: string;
  productName?: string;
  productSku?: string;
  status: 'pending' | 'not_matched' | 'on_hand' | 'consumed' | 'removed' | 'abandon';
  location?: string;
  smartAreaLocation?: string;
  quantity: number;
  matchedBy?: string;
  matchedAt?: string;
  notes?: string;
}

export interface UnifiedProduct {
  id: string;
  sku: string;
  name: string;
  description?: string;
  category: string;
  manufacturer?: string;
  image?: string;
  price?: number;
  unitCode?: string;
  status: 'active' | 'inactive';
}

export interface UnifiedSearchOptions {
  query?: string;
  category?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

/**
 * This is the interface that ALL parts of your app will use.
 * Your components import this and call these methods.
 * Behind the scenes, it figures out which API to call.
 */
export interface UnifiedApiService {
  // Tag Operations
  scanTag(rfidNumber: string): Promise<UnifiedTagInfo | null>;
  createTagMatch(productId: string, rfidNumber: string, quantity: number): Promise<UnifiedTagInfo>;
  updateTagStatus(rfidNumber: string, status: UnifiedTagInfo['status'], notes?: string): Promise<void>;
  updateTagQuantity(tagId: string, rfidNumber: string, newQuantity: number): Promise<void>;
  
  // RFID Tag Management
  listRfidTags(options?: { 
    page?: number; 
    pageSize?: number; 
    status?: string; 
    startDate?: string; 
    endDate?: string;
    getAllPages?: boolean;
    sortBy?: 'latest' | 'status';
    search?: string;
  }): Promise<{ tags: UnifiedTagInfo[]; total: number; totalPages: number; currentPage: number }>;
  updateRfidTag(tagId: string, updates: Partial<UnifiedTagInfo>): Promise<void>;
  deleteRfidTag(tagId: string, rfidNumber: string): Promise<void>;
  clearTag(tagId: string, rfidNumber: string): Promise<void>;
  assignRfidToProduct(tagId: string, productId: string): Promise<void>;
  resetRfidTag(tagId: string, resetType?: 'soft' | 'hard'): Promise<void>;
  loadLocationDataForTags(rfids: string[]): Promise<Map<string, any>>;
  replaceRfidTag(oldRfid: string, newRfid: string): Promise<void>;
  changeTagProduct(rfid: string, newProductId: string): Promise<void>;
  
  // Product Operations
  searchProducts(searchOptions: UnifiedSearchOptions): Promise<UnifiedProduct[]>;
  getProductById(productId: string): Promise<UnifiedProduct | null>;
  getProductCategories(): Promise<string[]>;
  
  // (2026-04-16, Jason Cox) Manual Checkout (hand scanner) -- Smart-only feature.
  lookupCheckoutTags?(rfids: string[]): Promise<ManualCheckoutTagInfo[]>;
  submitManualCheckout?(payload: ManualCheckoutPayload): Promise<ManualCheckoutResult>;
  getManualCheckoutHistory?(options?: ManualCheckoutHistoryOptions): Promise<ManualCheckoutHistoryResult>;

  // (2026-04-22, Jason Cox) Device Status -- Smart-only, reads existing md/v1/listSmartBox.
  getSmartboxStatusList?(filters?: SmartboxStatusFilters): Promise<SmartboxStatusResult>;

  // (2026-04-22, Jason Cox) Delivery Tickets (Consumption Events in UI) -- Smart-only, reads existing scm/v1/listDeliverTicket.
  getDeliveryTicketList?(filters?: DeliveryTicketFilters): Promise<DeliveryTicketResult>;

  // (2026-04-23, Jason Cox) Transfer Tickets (Restock Events in UI) -- Smart-only, reads existing scm/v1/listTransferTicket.
  getTransferTicketList?(filters?: TransferTicketFilters): Promise<TransferTicketResult>;

  // (2026-04-24, Jason Cox) Inventory per-smartbox -- Smart-only, reads existing md/v1/listBaseRfid.
  getBaseRfidList?(filters?: BaseRfidFilters): Promise<BaseRfidResult>;

  // Utility Methods
  getCurrentSystem(): 'smart' | 'sws';
  getCurrentUser(): User | null;
  isProductionEnvironment?(): boolean;
}
/**
 * Factory function to create the unified API service
 * This determines which system the user is on and returns the appropriate service
 */
export function createUnifiedApiService(user: User): UnifiedApiService {
  const system = user.system || 'smart';
  
  // For Smart system
  if (system === 'smart') {
    const authToken = storageService.getItem<string>('smartAccessToken') || storageService.getItem<string>('authToken') || '';
    
    console.log('🔑 Creating Smart API service with token:', {
      hasToken: !!authToken,
      tokenLength: authToken.length,
      user: user.username
    });
    
    return new SmartApiAdapter(authToken, user);
  } else {
    // For SWS system - use storageService to get tokens with proper prefix
    let authToken = storageService.getItem<string>('swsAccessToken') || storageService.getItem<string>('authToken') || '';
    
    // Debug: Check all possible token storage locations using both methods
    console.log('🔍 Debug: Token storage comparison:', {
      storageService_swsAccessToken: storageService.getItem<string>('swsAccessToken'),
      storageService_authToken: storageService.getItem<string>('authToken'),
      localStorage_swsAccessToken: localStorage.getItem('swsAccessToken'),
      localStorage_authToken: localStorage.getItem('authToken'),
      storageServiceKeys: storageService.getAllKeys()
    });
    
    console.log('🔑 Creating SWS API service with token:', {
      hasToken: !!authToken,
      tokenLength: authToken?.length || 0,
      user: user.username,
      tokenPreview: authToken ? authToken.substring(0, 8) + '...' : 'None'
    });
    
    return new SWSApiAdapter(authToken || '', user);
  }
}

/**
 * Smart System API Adapter
 * Translates unified API calls to Smart system API calls
 */
class SmartApiAdapter implements UnifiedApiService {
  private config: ApiConfig;
  private user: User;

  constructor(authToken: string, user: User) {
    this.user = user;
    const appConfig = configService.getConfig();
    this.config = {
      baseUrl: configService.getSmartApiUrl(),
      authHeaders: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'access-token': authToken,
        'token': authToken  // Some endpoints might use 'token' instead of 'access-token'
      },
      endpoints: {
        scanTag: '/md/v1/getAppPdaScanRfid',
        addTag: '/md/v1/tagItems', // Updated to use working endpoint
        editTag: '/md/v1/editAppPdaRfidItmes',
        productList: '/md/v1/getAppProductList',
        itemsList: '/md/v1/listItem',
        listBaseRfid: '/md/v1/listBaseRfid',
        categoryItems: '/md/v2/getListItems',
        categories: '/md/v2/listCategory'
      }
    };
  }

  async scanTag(rfidNumber: string): Promise<UnifiedTagInfo | null> {
    const logger = getSentryLogger();
    
    return trackApiCall(`${this.config.baseUrl}${this.config.endpoints.scanTag}`, 'POST', async () => {
      try {
        addUserActionBreadcrumb('Scanned RFID tag', { rfidNumber });
        
        const requestBody = {
          rfid: rfidNumber,
          smartboxId: "0",
          workcenterId: "0"
        };
        
        const response = await fetchWithRetry(
          `${this.config.baseUrl}${this.config.endpoints.scanTag}`,
          {
            method: 'POST',
            headers: this.config.authHeaders,
            body: JSON.stringify(requestBody)
          },
          {
            maxRetries: 2,
            timeout: 10000,
            onRetry: (attempt, error) => {
              logger.debug(logger.fmt`Retrying tag scan (attempt ${attempt}/2)`, { 
                error: error.message,
                rfidNumber 
              });
            }
          }
        );

      const data = await response.json();
      
      console.log('📡 Smart scanTag response:', {
        code: data.code,
        hasData: !!data.data,
        dataKeys: data.data ? Object.keys(data.data) : [],
        rfidNumber: rfidNumber
      });
      
        if (data.code === 0 && data.data) {
          // Tag found - return normalized tag info
          logger.info(logger.fmt`Smart tag found for RFID: ${rfidNumber}`);
          return this.normalizeTagInfo(data.data);
        } else if (data.code === 10008) {
          // Tag not found - this is expected for unmatched tags
          logger.debug(logger.fmt`Smart tag not found (code 10008) for RFID: ${rfidNumber}`);
          return null;
        } else {
          // Other error
          logger.warn("Smart scanTag unexpected response", { 
            code: data.code, 
            rfidNumber 
          });
          return null;
        }
      } catch (error) {
        logger.error("Smart API scanTag error", { 
          error: error instanceof Error ? error.message : String(error),
          rfidNumber 
        });
        captureExceptionWithContext(error, {
          tags: { api_system: 'smart', operation: 'scanTag' },
          extra: { rfidNumber }
        });
        throw new Error('Failed to scan tag: ' + (error instanceof Error ? error.message : String(error)));
      }
    });
  }

  async createTagMatch(productId: string, rfidNumber: string, quantity: number): Promise<UnifiedTagInfo> {
    const logger = getSentryLogger();
    
    return trackApiCall(`${this.config.baseUrl}${this.config.endpoints.addTag}`, 'POST', async () => {
      try {
        addUserActionBreadcrumb('Creating tag match', { productId, rfidNumber, quantity });
        
        const requestBody = {
          itemId: parseInt(productId),
          rfid: rfidNumber,
          packageQty: quantity,
          batchNum: `PWA-${Date.now()}`
        };
        
        const response = await fetchWithRetry(
          `${this.config.baseUrl}${this.config.endpoints.addTag}`,
          {
          method: 'POST',
          headers: this.config.authHeaders,
          body: JSON.stringify(requestBody)
        },
        {
          maxRetries: 3,
          timeout: 10000,
          onRetry: (attempt, error) => {
            console.log(`🔄 Retrying tag creation (attempt ${attempt}/3):`, error.message);
          }
        }
      );
      
        const data = await response.json();
        
        if (data.code === 0) {
          // Success - tag created
          logger.info(logger.fmt`Smart tag created successfully for RFID: ${rfidNumber}`);
          return {
            id: `${productId}_${rfidNumber}`,
            rfidNumber,
            productId: String(productId), // Ensure it's a string
            status: 'on_hand', // Smart tags start as on_hand when created
            quantity,
            matchedBy: this.user.username,
            matchedAt: new Date().toISOString(),
            location: undefined,
            notes: `Created via PWA with batch ${requestBody.batchNum}`
          };
        } else if (data.code === 10009) {
          // Tag already exists - provide detailed error with existing tag info
          const existingTag = data.data?.[0];
          if (existingTag) {
            throw new Error(`Tag ${rfidNumber} is already matched to product ${existingTag.itemId} (${existingTag.sku || 'Unknown SKU'})`);
          } else {
            throw new Error(`Tag ${rfidNumber} is already matched to another product`);
          }
        } else {
          // Other error
          throw new Error(data.msg || `Failed to create tag match (code: ${data.code})`);
        }
      } catch (error) {
        logger.error("Smart API createTagMatch error", { 
          error: error instanceof Error ? error.message : String(error),
          productId,
          rfidNumber 
        });
        
        // Re-throw with more context if it's our custom error
        if (error instanceof Error && error.message.includes('already matched')) {
          throw error;
        }
        
        captureExceptionWithContext(error, {
          tags: { api_system: 'smart', operation: 'createTagMatch' },
          extra: { productId, rfidNumber, quantity }
        });
        
        throw new Error('Failed to create tag match: ' + (error instanceof Error ? error.message : String(error)));
      }
    });
  }

  async updateTagStatus(rfidNumber: string, status: UnifiedTagInfo['status'], notes?: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}${this.config.endpoints.editTag}`, {
        method: 'POST',
        headers: this.config.authHeaders,
        body: JSON.stringify({
          rfid: rfidNumber,
          status: this.mapStatusToSmart(status),
          notes: notes || ''
        })
      });

      const data = await response.json();
      
      if (data.code !== 200) {
        throw new Error(data.msg || 'Failed to update tag status');
      }
    } catch (error) {
      console.error('Smart API updateTagStatus error:', error);
      throw new Error('Failed to update tag status');
    }
  }

  async updateTagQuantity(tagId: string, rfidNumber: string, newQuantity: number): Promise<void> {
    try {
      
      
      // First, get the current tag data (like Smart app does)
      
      const scanResponse = await fetch(`${this.config.baseUrl}${this.config.endpoints.scanTag}`, {
        method: 'POST',
        headers: this.config.authHeaders,
        body: JSON.stringify({
          rfid: rfidNumber,
          workcenterId: '0'
        })
      });

      const scanData = await scanResponse.json();
      
      
      if (scanData.code !== 0 || !scanData.data) {
        throw new Error('Could not find Smart tag data for update');
      }

      const tagData = scanData.data;
      
      // Use the full tag object with updated packageQty (like Smart app does)
      const requestBody = {
        ...tagData,
        packageQty: newQuantity.toString() // Smart app sends as string
      };
      
      
      
      const response = await fetch(`${this.config.baseUrl}/md/v1/rfid/updatePackageQty`, {
        method: 'POST',
        headers: this.config.authHeaders,
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      
      if (data.code === 0 || data.code === 200) {
        
      } else {
        throw new Error(data.msg || `Failed to update tag quantity (code: ${data.code})`);
      }
    } catch (error) {
      console.error('? Smart API updateTagQuantity error:', error);
      throw new Error('Failed to update tag quantity: ' + (error instanceof Error ? error.message : String(error)));
    }
  }


  async searchProducts(searchOptions: UnifiedSearchOptions): Promise<UnifiedProduct[]> {
    try {
      
      
      // Build base request body for server-side search
      let baseRequestBody: any = {
        search: searchOptions.query || '', // SERVER-SIDE search parameter
        smartboxId: "0",
        workcenterId: "0"
      };
      
      // Add category filtering if requested
      if (searchOptions.category) {
        
        try {
          const categoryList = await this.getCategoriesWithIds();
          const categoryObj = categoryList.find(cat => cat.name === searchOptions.category);
          if (categoryObj) {
            baseRequestBody.categoryId = categoryObj.id;
            
          } else {
            
          }
        } catch (error) {
          
        }
      }
      
      // Fetch ALL results using pagination - no matter how many (even 16M+)
      const allProducts: any[] = [];
      let page = 1;
      let hasMore = true;
      let totalReported = null;
      const pageSize = 100; // Use proven working page size from tests
      
      
      
      while (hasMore) {
        const requestBody = {
          ...baseRequestBody,
          page,
          pageSize
        };
        
        
        
        const response = await fetch(`${this.config.baseUrl}${this.config.endpoints.itemsList}`, {
          method: 'POST',
          headers: this.config.authHeaders,
          body: JSON.stringify(requestBody)
        });

        const data = await response.json();
        
        if (data.code === 0) {
          // Parse response structure
          let productsList = [];
          
          if (data.data?.data && Array.isArray(data.data.data)) {
            // SMART API uses data.data.data structure
            productsList = data.data.data;
          } else if (data.data?.list && Array.isArray(data.data.list)) {
            // Fallback for data.data.list structure
            productsList = data.data.list;
          } else if (Array.isArray(data.data)) {
            // Fallback for direct data.data array
            productsList = data.data;
          } else {
            
            
            productsList = [];
          }

          // Track total count from API for verification
          if (totalReported === null && data.data?.total) {
            totalReported = data.data.total;
            
            if (totalReported > 10000) {
              
            }
          }
          
          
          
          if (productsList.length > 0) {
            allProducts.push(...productsList);
            
            // Continue pagination logic:
            // - If we got fewer products than requested, we're at the end
            // - Also check against total count if available for extra safety
            const expectingMore = productsList.length === pageSize;
            const haveAllFromTotal = totalReported && allProducts.length >= totalReported;
            
            hasMore = expectingMore && !haveAllFromTotal;
            
            if (hasMore) {
              page++;
              
              // Progress indicator for large datasets
              if (page % 10 === 0) {
                const progress = totalReported ? `${allProducts.length}/${totalReported}` : `${allProducts.length}`;
                
              }
            } else {
              if (haveAllFromTotal) {
                
              } else {
                
              }
            }
          } else {
            hasMore = false;
            
          }
        } else if (data.code === 10018) {
          throw new Error('Authentication required - please log in with valid Smart credentials');
        } else {
          throw new Error(data.msg || `Smart API error: code ${data.code}`);
        }

        // Safety check to prevent infinite loops (but allow very large datasets)
        if (page > 200000) { // Allow up to 20M results (200k pages * 100 per page)
          
          
          break;
        }
      }
      
      // Convert all products to unified format
      const unifiedProducts = allProducts.map((item: any) => this.normalizeProduct(item));
      
      
      
      
      
      
      
      return unifiedProducts;
      
    } catch (error: any) {
      console.error('❌ Smart API server-side searchProducts error:', error);
      throw error;
    }
  }

  /**
   * Temporary method to map products to categories based on SKU patterns
   * This is a workaround until the v2 API endpoint is working
   */
  private mapProductToCategory(product: UnifiedProduct, categoryName: string): boolean {
    // Safely handle undefined properties
    const sku = (product.sku || '').toUpperCase();
    const name = (product.name || '').toUpperCase();
    const category = categoryName.toUpperCase();
    
    // If both sku and name are empty, can't match anything
    if (!sku && !name) {
      return false;
    }
    
    // Basic category mapping based on SKU patterns
    // This is a temporary solution - ideally this mapping should come from the API
    switch (category) {
      case 'SS': // Stainless Steel category
        // Products with SS in name/description or specific SKU patterns
        return sku.includes('SS') || 
               name.includes('STAINLESS') ||
               name.includes(' SS ') ||
               sku.startsWith('S1') || // S1UEI-4 pattern
               sku.includes('SUPERLOK');
               
      case 'FS': // Fitting/Seal category maybe?
        return sku.startsWith('FS') || 
               name.includes('FITTING') ||
               name.includes('SEAL');
               
      case 'GAU': // Gauge category maybe?
        return sku.startsWith('GAU') || 
               sku.includes('GAUGE') ||
               name.includes('GAUGE') ||
               name.includes('METER');
               
      case 'NIP': // Nipple category
        return sku.startsWith('N') || 
               name.includes('NIPPLE') ||
               name.includes('PIPE NIPPLE');
               
      case 'MISC': // Miscellaneous
        // Default fallback for unmatched items
        return !this.mapProductToCategory(product, 'SS') && 
               !this.mapProductToCategory(product, 'FS') && 
               !this.mapProductToCategory(product, 'GAU') && 
               !this.mapProductToCategory(product, 'NIP');
               
      default:
        // For other categories, try basic name matching
        return name.includes(category) || 
               sku.includes(category);
    }
  }

  async getProductById(productId: string): Promise<UnifiedProduct | null> {
    // Implementation would call Smart API to get specific product
    return null;
  }

  async getProductCategories(): Promise<string[]> {
    try {
      
      
      const requestBody: any = {
        page: 1,
        pageSize: 100,
        smartboxId: "0",
        workcenterId: "0"
      };
      
      // Add companyId filter if available
      if (this.user.companyId) {
        requestBody.companyId = this.user.companyId;
        console.log('🏢 Smart API: Filtering categories by companyId:', this.user.companyId);
      }
      
      const response = await fetch(`${this.config.baseUrl}${this.config.endpoints.categories}`, {
        method: 'POST',
        headers: this.config.authHeaders,
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      
      if (data.code === 0 && data.data) {
        // Filter categories by companyId if it's in the response
        let categories = data.data;
        
        if (this.user.companyId) {
          const userCompanyId = this.user.companyId;
          categories = categories.filter((cat: any) => 
            !cat.companyId || 
            cat.companyId === userCompanyId || 
            cat.companyId === parseInt(userCompanyId)
          );
          console.log(`🏢 Filtered categories: ${categories.length} out of ${data.data.length} for company ${userCompanyId}`);
        }
        
        const categoryNames = categories.map((cat: any) => cat.name);
        
        return categoryNames;
      } else if (data.code === 10018) {
        
        return []; // No hardcoded fallbacks
      } else {
        console.error('❌ Smart API categories error:', data);
        return [];
      }
    } catch (error: any) {
      console.error('❌ Smart API getProductCategories error:', error);
      
      
      return []; // No hardcoded fallbacks
    }
  }

  async getCategoriesWithIds(): Promise<Array<{id: number, name: string}>> {
    try {
      const requestBody: any = {
        page: 1,
        pageSize: 100,
        smartboxId: "0",
        workcenterId: "0"
      };
      
      // Add companyId filter if available
      if (this.user.companyId) {
        requestBody.companyId = this.user.companyId;
        console.log('🏢 Smart API getCategoriesWithIds: Filtering by companyId:', this.user.companyId);
      }
      
      const response = await fetch(`${this.config.baseUrl}${this.config.endpoints.categories}`, {
        method: 'POST',
        headers: this.config.authHeaders,
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      if (data.code === 0 && data.data) {
        // Smart categories API returns array of objects with 'id' and 'name' fields
        if (Array.isArray(data.data)) {
          let categories = data.data;
          
          // Filter by companyId if available
          if (this.user.companyId) {
            const userCompanyId = this.user.companyId;
            categories = categories.filter((cat: any) => 
              !cat.companyId || 
              cat.companyId === userCompanyId || 
              cat.companyId === parseInt(userCompanyId)
            );
            console.log(`🏢 Filtered categories with IDs: ${categories.length} out of ${data.data.length} for company ${userCompanyId}`);
          }
          
          const mappedCategories = categories.map((cat: any) => ({
            id: cat.id,
            name: cat.name
          })).filter((cat: any) => cat.id && cat.name);
          
          return mappedCategories;
        }
      }
      
      
      return [];
    } catch (error) {
      console.error('❌ Smart API getCategoriesWithIds error:', error);
      throw new Error('Failed to fetch categories with IDs');
    }
  }

  // RFID Tag Management Methods
  async listRfidTags(options: { 
    page?: number; 
    pageSize?: number; 
    status?: string; 
    startDate?: string; 
    endDate?: string;
    getAllPages?: boolean;
    sortBy?: 'latest' | 'status';
    search?: string;
  } = {}): Promise<{ tags: UnifiedTagInfo[]; total: number; totalPages: number; currentPage: number }> {
    try {
      const endpoint = this.config.endpoints.listBaseRfid;
      
      // If getAllPages is true, we'll fetch all pages
      if (options.getAllPages) {
        let allTags: UnifiedTagInfo[] = [];
        let currentPage = 1;
        let hasMore = true;
        const pageSize = 1000; // Fetch 1000 at a time for efficiency
        
        while (hasMore) {
          const requestBody = {
            page: currentPage,
            pageSize: pageSize,
            sort: 0,  // 0 = desc, 1 = asc (required by Smart API)
            field: 'id',  // Default sort field
            ...(options.status && { status: options.status }),
            ...(options.startDate && { startDate: options.startDate }),
            ...(options.endDate && { endDate: options.endDate }),
            ...(options.search && { rfid: options.search }) // SMART uses exact RFID match
          };
          
          
          
          const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
            method: 'POST',
            headers: this.config.authHeaders,
            body: JSON.stringify(requestBody)
          });
          const data = await response.json();
          
          if ((data.code === 200 || data.code === 0) && data.data) {
            const rfidTags = Array.isArray(data.data.data) ? data.data.data : 
                            Array.isArray(data.data) ? data.data : [];
            
            const normalizedTags = rfidTags.map((tag: any) => this.normalizeRfidTag(tag));
            allTags = allTags.concat(normalizedTags);
            
            // Check if there are more pages
            const totalPages = Math.ceil((data.data.total || 0) / pageSize);
            hasMore = currentPage < totalPages;
            currentPage++;
            
            
          } else {
            hasMore = false;
          }
        }
        
        return {
          tags: allTags,
          total: allTags.length,
          totalPages: 1,
          currentPage: 1
        };
      } else {
        // Single page request
        const requestBody = {
          page: options.page || 1,
          pageSize: options.pageSize || 50,
          sort: 0,  // 0 = desc, 1 = asc (required by Smart API)
          field: 'id',  // Default sort field
          ...(options.status && { status: options.status }),
          ...(options.search && { rfid: options.search }) // SMART uses exact RFID match
        };
        
        
        
        
        
        const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
          method: 'POST',
          headers: this.config.authHeaders,
          body: JSON.stringify(requestBody)
        });
        const data = await response.json();
        
        
        if ((data.code === 200 || data.code === 0) && data.data) {
          // Smart API returns paginated data in data.data.data array
          const rfidTags = Array.isArray(data.data.data) ? data.data.data : 
                          Array.isArray(data.data) ? data.data : [];
          
          const normalizedTags = rfidTags.map((tag: any) => this.normalizeRfidTag(tag));
          
          return {
            tags: normalizedTags,
            total: data.data.total || 0,
            totalPages: Math.ceil((data.data.total || 0) / (options.pageSize || 50)),
            currentPage: options.page || 1
          };
        }
      }
      
      return {
        tags: [],
        total: 0,
        totalPages: 0,
        currentPage: 1
      };
    } catch (error) {
      console.error('❌ Smart API listRfidTags error:', error);
      throw new Error('Failed to list RFID tags');
    }
  }

  async updateRfidTag(tagId: string, updates: Partial<UnifiedTagInfo>): Promise<void> {
    try {
      const endpoint = '/md/v1/updateRfid';
      const requestBody = {
        rfidId: parseInt(tagId),
        ...(updates.quantity && { packageQty: updates.quantity }),
        ...(updates.status && { status: this.mapStatusToSmart(updates.status) }),
        ...(updates.notes && { notes: updates.notes })
      };
      
      
      
      const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: this.config.authHeaders,
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      
      if (data.code !== 200) {
        throw new Error(data.message || 'Failed to update RFID tag');
      }
    } catch (error) {
      console.error('❌ Smart API updateRfidTag error:', error);
      throw new Error('Failed to update RFID tag');
    }
  }

  async deleteRfidTag(tagId: string, rfidNumber: string): Promise<void> {
    try {
      
      
      const endpoint = '/md/v1/delBaseRfid';
      const requestBody = {
        rfid: [rfidNumber] // Smart expects array of RFID numbers
      };
      
      
      
      const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: this.config.authHeaders,
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      
      if (data.code === 0) {
        
      } else {
        throw new Error(data.msg || `Failed to clear tag (code: ${data.code})`);
      }
    } catch (error) {
      console.error('❌ Smart API deleteRfidTag error:', error);
      throw new Error('Failed to clear tag: ' + (error instanceof Error ? error.message : String(error)));
    }
  }

  async clearTag(tagId: string, rfidNumber: string): Promise<void> {
    // For Smart, clearing a tag is the same as deleting it
    await this.deleteRfidTag(tagId, rfidNumber);
  }

  async assignRfidToProduct(tagId: string, productId: string): Promise<void> {
    try {
      const endpoint = '/scm/v1/rfid/assignTag';
      const requestBody = {
        rfidId: parseInt(tagId),
        productId: parseInt(productId),
        quantity: 1
      };
      
      
      
      const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: this.config.authHeaders,
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      
      if (data.code !== 200) {
        throw new Error(data.message || 'Failed to assign RFID to product');
      }
    } catch (error) {
      console.error('❌ Smart API assignRfidToProduct error:', error);
      throw new Error('Failed to assign RFID to product');
    }
  }

  async resetRfidTag(tagId: string, resetType: 'soft' | 'hard' = 'soft'): Promise<void> {
    try {
      const endpoint = '/md/v1/restBaseRfid';
      const requestBody = {
        rfidId: parseInt(tagId),
        resetType: resetType
      };
      
      
      
      const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: this.config.authHeaders,
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      
      if (data.code !== 200) {
        throw new Error(data.message || 'Failed to reset RFID tag');
      }
    } catch (error) {
      console.error('❌ Smart API resetRfidTag error:', error);
      throw new Error('Failed to reset RFID tag');
    }
  }

  async replaceRfidTag(oldRfid: string, newRfid: string): Promise<void> {
    try {
      // Use the editAppPdaRfidItmes endpoint with type 2 for replacing RFID
      const requestBody = {
        rfid: oldRfid,
        type: 2,
        itemsId: newRfid // For type 2, itemsId is the new RFID number
      };
      
      console.log('🔄 Smart API replaceRfidTag:', { oldRfid, newRfid });
      
      const response = await fetch(`${this.config.baseUrl}${this.config.endpoints.editTag}`, {
        method: 'POST',
        headers: this.config.authHeaders,
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      console.log('📦 Smart API replaceRfidTag response:', data);
      
      if (data.code !== 0) {
        throw new Error(data.msg || 'Failed to replace RFID tag');
      }
    } catch (error) {
      console.error('❌ Smart API replaceRfidTag error:', error);
      throw new Error('Failed to replace RFID tag: ' + (error instanceof Error ? error.message : String(error)));
    }
  }

  async changeTagProduct(rfid: string, newProductId: string): Promise<void> {
    try {
      // Use the editAppPdaRfidItmes endpoint with type 1 for changing product
      const requestBody = {
        rfid: rfid,
        type: 1,
        itemsId: newProductId // For type 1, itemsId is the new product ID
      };
      
      console.log('🔄 Smart API changeTagProduct:', { rfid, newProductId });
      
      const response = await fetch(`${this.config.baseUrl}${this.config.endpoints.editTag}`, {
        method: 'POST',
        headers: this.config.authHeaders,
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      console.log('📦 Smart API changeTagProduct response:', data);
      
      if (data.code !== 0) {
        throw new Error(data.msg || 'Failed to change product association');
      }
    } catch (error) {
      console.error('❌ Smart API changeTagProduct error:', error);
      throw new Error('Failed to change product association: ' + (error instanceof Error ? error.message : String(error)));
    }
  }

  // Helper method to normalize Smart RFID tag data to unified format
  private normalizeRfidTag(smartTagData: any): UnifiedTagInfo {
    // Debug status mapping
    
    const mappedStatus = this.mapStatusFromSmart(smartTagData.status);
    
    
    return {
      id: smartTagData.id?.toString() || smartTagData.rfidId?.toString(),
      rfidNumber: smartTagData.rfid || smartTagData.rfidNumber,
      productId: smartTagData.sku?.toString() || smartTagData.itemId?.toString() || smartTagData.productId?.toString(),
      productName: smartTagData.Name || smartTagData.itemName || smartTagData.productName,
      productSku: smartTagData.sku || smartTagData.productSku,
      status: mappedStatus,
      location: smartTagData.wname, // Primary location (workcenter)
      smartAreaLocation: smartTagData.sName, // Secondary location (smartbox)
      quantity: smartTagData.packageQty || smartTagData.quantity || 1,
      notes: smartTagData.notes || smartTagData.note,
      matchedBy: smartTagData.createdBy,
      matchedAt: smartTagData.createAt ? new Date(smartTagData.createAt * 1000).toISOString() : smartTagData.matchedAt
    };
  }

  loadLocationDataForTags(rfids: string[]): Promise<Map<string, any>> {
    // Smart system doesn't need separate location loading - return empty map
    return Promise.resolve(new Map());
  }

  getCurrentSystem(): 'smart' | 'sws' {
    return 'smart';
  }

  getCurrentUser(): User | null {
    return this.user;
  }

  /**
   * Smart API doesn't have the production/testing issue
   * Always return true for compatibility
   */
  isProductionEnvironment(): boolean {
    return true; // Smart API works correctly in all environments
  }

  // ---------------------------------------------------------------------
  // Extracted feature endpoints -- (2026-04-24, Jason Cox)
  // These were per-method HTTP calls that lived inline in this class and
  // have been moved into src/services/smart/*Api.ts alongside their types.
  // The class now delegates. Kept on the class so UnifiedApiService
  // consumers see the same methods they always did.
  // ---------------------------------------------------------------------

  lookupCheckoutTags(rfids: string[]): Promise<ManualCheckoutTagInfo[]> {
    return smartLookupCheckoutTags(this.config, rfids);
  }

  submitManualCheckout(payload: ManualCheckoutPayload): Promise<ManualCheckoutResult> {
    return smartSubmitManualCheckout(this.config, payload);
  }

  getManualCheckoutHistory(options: ManualCheckoutHistoryOptions = {}): Promise<ManualCheckoutHistoryResult> {
    return smartGetManualCheckoutHistory(this.config, options);
  }

  getDeliveryTicketList(filters: DeliveryTicketFilters = {}): Promise<DeliveryTicketResult> {
    return smartGetDeliveryTicketList(this.config, filters);
  }

  getTransferTicketList(filters: TransferTicketFilters = {}): Promise<TransferTicketResult> {
    return smartGetTransferTicketList(this.config, filters);
  }

  getSmartboxStatusList(filters: SmartboxStatusFilters = {}): Promise<SmartboxStatusResult> {
    return smartGetSmartboxStatusList(this.config, filters);
  }

  getBaseRfidList(filters: BaseRfidFilters = {}): Promise<BaseRfidResult> {
    return smartGetBaseRfidList(this.config, filters);
  }

  // Helper methods to normalize Smart API responses to unified format
  private normalizeTagInfo(smartTagData: any): UnifiedTagInfo {
    // Log raw data for debugging
    console.log('🔍 Smart normalizeTagInfo raw data:', smartTagData);
    
    // Ensure productId is always a string for consistent comparison
    let productId: string | undefined;
    if (smartTagData.itemId !== undefined && smartTagData.itemId !== null) {
      productId = String(smartTagData.itemId);
    } else if (smartTagData.productId !== undefined && smartTagData.productId !== null) {
      productId = String(smartTagData.productId);
    }
    
    const normalized = {
      id: smartTagData.id?.toString() || smartTagData.rfid,
      rfidNumber: smartTagData.rfid || smartTagData.tagNumber,
      productId: productId,
      productName: smartTagData.itemName || smartTagData.Name,
      productSku: smartTagData.sku,
      status: this.mapStatusFromSmart(smartTagData.status),
      location: smartTagData.location,
      quantity: smartTagData.quantity || smartTagData.packageQty || 1,
      matchedBy: smartTagData.matchedBy,
      matchedAt: smartTagData.matchedAt,
      notes: smartTagData.notes
    };
    
    console.log('📦 Smart normalized tag info:', normalized);
    return normalized;
  }

  private normalizeProduct(smartProductData: any): UnifiedProduct {
    // Reduced logging for better performance
    
    // v1/listItem includes categoryName, categoryCode, and categoryId
    const category = smartProductData.categoryName || 
                    smartProductData.categoryCode ||
                    smartProductData.category || 
                    smartProductData.Category ||
                    '';

    // Category mapping completed
    
    // Map Smart itemStatus: 0 = active, 1 = inactive
    const productStatus = smartProductData.itemStatus === 0 ? 'active' : 'inactive';
    
    return {
      id: smartProductData.id?.toString(),
      sku: smartProductData.sku,
      name: smartProductData.Name || smartProductData.itemName,
      description: smartProductData.description,
      category: category,
      manufacturer: smartProductData.manufacturer,
      image: smartProductData.image,
      price: parseFloat(smartProductData.price || '0'),
      unitCode: smartProductData.unitCode,
      status: productStatus
    };
  }

  private mapStatusToSmart(status: UnifiedTagInfo['status']): string {
    const statusMap = {
      'pending': 'pending',
      'not_matched': 'not_matched',
      'on_hand': 'on_hand',
      'consumed': 'consumed',
      'removed': 'removed',
      'abandon': 'removed' // Map abandon to removed for Smart system compatibility
    };
    return statusMap[status] || 'pending';
  }

  private mapStatusFromSmart(smartStatus: any): UnifiedTagInfo['status'] {
    // Map Smart system status to unified status
    if (typeof smartStatus === 'string') {
      switch (smartStatus.toLowerCase()) {
        case 'on_hand': return 'on_hand';
        case 'consumed': return 'consumed';
        case 'removed': return 'removed';
        default: return 'pending';
      }
    }
    
    // Handle numeric status codes from Smart API
    if (typeof smartStatus === 'number') {
      switch (smartStatus) {
        case 0: return 'pending';     // Prepare
        case 1: return 'not_matched'; // Tag not matched
        case 2: return 'pending';     // Pending/awaiting processing
        case 3: return 'on_hand';     // On hand
        case 4: return 'consumed';    // Consumed/used
        default: return 'pending';    // Unknown cases default to pending
      }
    }
    
    return 'pending';
  }

  private getFallbackProductsForCategories(): UnifiedProduct[] {
    // Demo products that match the actual categories from Smart API
    // Based on the 18 categories we successfully retrieved
    return [
      {
        id: 'smart-demo-fs-1',
        sku: 'FS-001',
        name: 'Field Service Tool Kit',
        description: 'Complete field service tool kit for maintenance',
        category: 'FS',
        manufacturer: 'FieldPro',
        price: 125.99,
        unitCode: 'kit',
        status: 'active'
      },
      {
        id: 'smart-demo-gau-1',
        sku: 'GAU-002',
        name: 'Pressure Gauge',
        description: 'High precision pressure gauge',
        category: 'GAU',
        manufacturer: 'GaugeTech',
        price: 45.50,
        unitCode: 'ea',
        status: 'active'
      },
      {
        id: 'smart-demo-misc-1',
        sku: 'MISC-003',
        name: 'General Purpose Supplies',
        description: 'Miscellaneous supplies for general use',
        category: 'MISC',
        manufacturer: 'SupplyCorp',
        price: 35.25,
        unitCode: 'pack',
        status: 'active'
      },
      {
        id: 'smart-demo-nip-1',
        sku: 'NIP-004',
        name: 'Nipple Fitting',
        description: 'Standard nipple fitting for connections',
        category: 'NIP',
        manufacturer: 'FittingPro',
        price: 12.75,
        unitCode: 'ea',
        status: 'active'
      },
      {
        id: 'smart-demo-ss-1',
        sku: 'SS-005',
        name: 'Stainless Steel Components',
        description: 'Stainless steel parts and components',
        category: 'SS',
        manufacturer: 'SteelCorp',
        price: 89.99,
        unitCode: 'set',
        status: 'active'
      }
    ];
  }
}

/**
 * SWS System API Adapter
 * Translates unified API calls to SWS system API calls
 */
class SWSApiAdapter implements UnifiedApiService {
  private config: ApiConfig;
  private user: User;

  constructor(authToken: string, user: User) {
    this.user = user;
    const appId: string = storageService.getItem<string>('swsAppId') || 'wms_estis'; // Use stored appId from login
    
    
    
    // If no token provided, try to get it from storageService
    if (!authToken) {
      authToken = storageService.getItem<string>('swsAccessToken') || storageService.getItem<string>('authToken') || '';
      console.warn('⚠️ SWS API: No auth token provided, retrieved from storageService:', {
        hasToken: !!authToken,
        tokenLength: authToken?.length || 0
      });
    }
    
    const appConfig = configService.getConfig();
    this.config = {
      baseUrl: configService.getSwsApiUrl(),
      authHeaders: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'access-token': authToken, // Use access-token header (confirmed working)
        'appId': appId, // Dynamic appId from user login response
        'client': appId // Also include as client header
      },
      endpoints: {
        rfidTags: '/warehouse/rfidTags', // CONFIRMED: Working endpoint with appId 'wms_estis'
        addRfid: '/rfid/tagItems', // FIXED: Remove /api/v1 since baseUrl already includes it via proxy
        updateTag: '/rfid/updateTag', // Working SWS update endpoint
        deleteTag: '/rfid/deleteTags', // Working SWS delete endpoint
        products: '/product', // Full product catalog
        categories: '/category', // Category list (3 categories)
        categoryList: '/categoryList' // Extended category list (6 categories)
      }
    };
  }

  async scanTag(rfidNumber: string): Promise<UnifiedTagInfo | null> {
    try {
      
      
      // Build complete warehouse query parameters (required by SWS)
      const params = new URLSearchParams();
      params.append('rfid', rfidNumber);
      params.append('whsId', '-1'); // -1 means "all warehouses"
      params.append('status', '-1'); // -1 means "all statuses" 
      params.append('isabnormal', '-1'); // -1 means "all abnormal statuses"
      params.append('itemsStatus', '-1'); // -1 means "all item statuses"
      params.append('total', '0'); // Standard pagination param
      params.append('page', '1');
      params.append('pageSize', '10'); // Small page size for single tag lookup
      params.append('sku', '');
      params.append('name', '');
      
      const url = `${this.config.baseUrl}${this.config.endpoints.rfidTags}?${params.toString()}`;
      
      
      const response = await fetchWithRetry(
        url,
        {
          method: 'GET',
          headers: this.config.authHeaders
        },
        {
          maxRetries: 2,
          timeout: 10000,
          onRetry: (attempt, error) => {
            console.log(`🔄 Retrying SWS tag scan (attempt ${attempt}/2):`, error.message);
          }
        }
      );

      const data = await response.json();
      
      console.log('📡 SWS scanTag response:', {
        code: data.code,
        hasData: !!data.data,
        dataStructure: data.data ? {
          hasDataArray: !!data.data.data,
          dataLength: data.data.data?.length
        } : null,
        rfidNumber: rfidNumber
      });
      
      if ((data.code === 200 || data.code === 0) && data.data?.data?.length > 0) {
        const tagData = data.data.data[0];
        console.log('✅ SWS tag found, raw data:', tagData);
        return this.normalizeTagInfo(tagData);
      }
      
      console.log('ℹ️ SWS tag not found or empty response');
      return null;
    } catch (error) {
      console.error('❌ SWS API scanTag error:', error);
      throw new Error('Failed to scan tag');
    }
  }

  async createTagMatch(productId: string, rfidNumber: string, quantity: number): Promise<UnifiedTagInfo> {
    try {
      
      
      const requestBody = {
        itemId: parseInt(productId),
        rfid: rfidNumber,  // SWS uses 'rfid' not 'rfidNumber'
        packageQty: quantity,  // SWS uses 'packageQty' not 'quantity'
        batchNum: `PWA-${Date.now()}`
      };
      
      
      
      const response = await fetchWithRetry(
        `${this.config.baseUrl}${this.config.endpoints.addRfid}`,
        {
          method: 'POST',
          headers: this.config.authHeaders,
          body: JSON.stringify(requestBody)
        },
        {
          maxRetries: 3,
          timeout: 10000,
          onRetry: (attempt, error) => {
            console.log(`🔄 Retrying SWS tag creation (attempt ${attempt}/3):`, error.message);
          }
        }
      );
      
      const data = await response.json();
      
      
      // Check HTTP status first
      if (response.status === 400) {
        // SWS API uses HTTP 400 for duplicate tags with message field
        if (data.message && data.message.includes('already exists')) {
          throw new Error(data.message); // Use exact error message from SWS
        } else {
          throw new Error(data.message || 'Bad request');
        }
      } else if (!response.ok) {
        // Other HTTP errors
        throw new Error(`HTTP ${response.status}: ${data.message || 'Request failed'}`);
      }
      
      // Success response handling
      if (data.code === 200 || data.code === 0 || response.status === 200) {
        // Success - tag created
        console.log('✅ SWS tag created successfully, returning productId as:', String(productId));
        return {
          id: `${productId}_${rfidNumber}`,
          rfidNumber,
          productId: String(productId), // Ensure it's a string
          status: 'pending', // SWS tags start as pending when created
          quantity,
          matchedBy: this.user.username,
          matchedAt: new Date().toISOString(),
          location: undefined,
          notes: `Created via PWA with batch ${requestBody.batchNum}`
        };
      } else {
        // Unexpected response format
        throw new Error(data.message || `Unexpected response (code: ${data.code})`);
      }
    } catch (error) {
      console.error('❌ SWS API createTagMatch error:', error);
      
      // Check if this is a fetch error vs our thrown error
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.error('🌐 Network/fetch error:', error);
        throw new Error('Network error: Unable to connect to SWS API');
      }
      
      // Re-throw our custom errors (like duplicate tag messages)
      if (error instanceof Error) {
        console.error('🔄 Re-throwing SWS error:', error.message);
        throw error; // Preserve the original error message
      }
      
      throw new Error('Failed to create tag match: ' + String(error));
    }
  }

  async updateTagStatus(rfidNumber: string, status: UnifiedTagInfo['status'], notes?: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}${this.config.endpoints.updateTag}`, {
        method: 'PUT',
        headers: this.config.authHeaders,
        body: JSON.stringify({
          rfid: rfidNumber,
          status: this.mapStatusToSWS(status),
          notes: notes || ''
        })
      });

      const data = await response.json();
      
      if (data.code !== 200) {
        throw new Error(data.msg || 'Failed to update tag status');
      }
    } catch (error) {
      console.error('SWS API updateTagStatus error:', error);
      throw new Error('Failed to update tag status');
    }
  }

  async updateTagQuantity(tagId: string, rfidNumber: string, newQuantity: number): Promise<void> {
    try {
      
      
      // First, get the current warehouse tag data
      
      const params = new URLSearchParams({
        whsId: '-1',
        status: '-1',
        isabnormal: '-1',
        itemsStatus: '-1',
        total: '0',
        page: '1',
        pageSize: '10',
        sku: '',
        name: '',
        rfid: rfidNumber
      });
      
      const warehouseResponse = await fetch(
        `${this.config.baseUrl}${this.config.endpoints.rfidTags}?${params.toString()}`,
        {
          method: 'GET',
          headers: this.config.authHeaders
        }
      );
      
      const warehouseData = await warehouseResponse.json();
      
      if (warehouseData.code !== 200 || !warehouseData.data?.data?.length) {
        throw new Error('Could not find warehouse tag data for update');
      }
      
      const warehouseTag = warehouseData.data.data[0];
      
      
      // Use the full warehouse object with updated quantity (like SWS app does)
      const requestBody = {
        ...warehouseTag,
        packageQty: newQuantity.toString() // Update quantity
      };
      
      
      
      const response = await fetch(`${this.config.baseUrl}${this.config.endpoints.updateTag}`, {
        method: 'PUT',
        headers: this.config.authHeaders,
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      
      if (data.code === 200 || data.code === 0) {
        
      } else {
        throw new Error(data.message || data.msg || `Failed to update tag quantity (code: ${data.code})`);
      }
    } catch (error) {
      console.error('? SWS API updateTagQuantity error:', error);
      throw new Error('Failed to update tag quantity: ' + (error instanceof Error ? error.message : String(error)));
    }
  }


  private allProductsCache: { products: UnifiedProduct[], timestamp: number } | null = null;
  private readonly ALL_PRODUCTS_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes for all products

  private async getAllProducts(): Promise<UnifiedProduct[]> {
    // Check if we have cached all products
    if (this.allProductsCache && Date.now() - this.allProductsCache.timestamp < this.ALL_PRODUCTS_CACHE_DURATION) {
      
      return this.allProductsCache.products;
    }

    
    const allProducts: any[] = [];
    let page = 1;
    const pageSize = 1000; // Reasonable page size for API
    let hasMore = true;
    
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 3;
    
    while (hasMore) {
      try {
        
        
        const response = await fetch(`${this.config.baseUrl}/product?page=${page}&pageSize=${pageSize}`, {
          method: 'GET',
          headers: this.config.authHeaders
        });

        if (!response.ok) {
          // Handle HTTP errors (500, 502, etc.)
          
          consecutiveErrors++;
          
          if (consecutiveErrors >= maxConsecutiveErrors) {
            
            break;
          }
          
          page++;
          continue;
        }

        const data = await response.json();
        
        if ((data.code === 200 || data.code === 0) && data.data) {
          const products = data.data.data || data.data;
          
          if (Array.isArray(products) && products.length > 0) {
            allProducts.push(...products);
            
            
            // Reset error counter on success
            consecutiveErrors = 0;
            
            // Check if we got fewer products than requested (last page)
            hasMore = products.length === pageSize;
            page++;
          } else {
            hasMore = false;
          }
        } else {
          
          consecutiveErrors++;
          
          if (consecutiveErrors >= maxConsecutiveErrors) {
            
            break;
          }
          
          page++;
        }
      } catch (error) {
        
        consecutiveErrors++;
        
        if (consecutiveErrors >= maxConsecutiveErrors) {
          
          break;
        }
        
        page++;
      }
    }
    
    // Return whatever products we successfully fetched
    if (allProducts.length > 0) {
      // Normalize all products
      const unifiedProducts = allProducts.map((item: any) => this.normalizeProduct(item));
      
      // Cache the complete dataset
      this.allProductsCache = {
        products: unifiedProducts,
        timestamp: Date.now()
      };
      
      
      return unifiedProducts;
    } else {
      console.error('❌ SWS API - No products could be fetched');
      return [];
    }
  }

  async searchProducts(searchOptions: UnifiedSearchOptions): Promise<UnifiedProduct[]> {
    try {
      
      
      // Build query parameters for server-side search
      const searchParams = new URLSearchParams();
      if (searchOptions.query && searchOptions.query.trim()) {
        searchParams.append('search', searchOptions.query.trim()); // SERVER-SIDE search parameter
      }
      
      // Add server-side category filtering
      if (searchOptions.category && searchOptions.category.trim()) {
        searchParams.append('categoryName', searchOptions.category.trim()); // SERVER-SIDE category filtering
        
      }
      
      // Fetch ALL results using pagination
      const allProducts: any[] = [];
      let page = 1;
      let hasMore = true;
      const pageSize = 1000; // Reasonable page size for API
      
      
      
      
      while (hasMore) {
        // Build URL with search and pagination parameters
        const currentParams = new URLSearchParams(searchParams);
        currentParams.append('page', page.toString());
        currentParams.append('pageSize', pageSize.toString());
        
        const url = `${this.config.baseUrl}/product?${currentParams.toString()}`;
        
        
        
        const response = await fetch(url, {
          method: 'GET',
          headers: this.config.authHeaders
        });

        const data = await response.json();
        
        if (data.code === 200 || data.code === 0) {
          // Parse response structure (SWS uses data.data.data)
          const productsList = data.data?.data || [];
          
          
          
          if (productsList.length > 0) {
            allProducts.push(...productsList);
            
            // Check if we should continue - if we got fewer products than requested, we're done
            hasMore = productsList.length === pageSize;
            page++;
          } else {
            hasMore = false;
          }
        } else {
          console.error(`❌ SWS API error on page ${page}:`, data);
          throw new Error(data.message || `SWS API error: code ${data.code}`);
        }
      }
      
      // Convert all products to unified format
      const unifiedProducts = allProducts.map((item: any) => this.normalizeProduct(item));
      
      
      
      
      
      
      
      return unifiedProducts;
      
    } catch (error) {
      console.error('❌ SWS API server-side searchProducts error:', error);
      return [];
    }
  }

  async getProductById(productId: string): Promise<UnifiedProduct | null> {
    // Implementation would call SWS API to get specific product
    return null;
  }

  async getProductCategories(): Promise<string[]> {
    try {
      
      
      // Use GET /category endpoint as you specified
      const response = await fetch(`${this.config.baseUrl}/category`, {
        method: 'GET',
        headers: this.config.authHeaders
      });

      const data = await response.json();
      
      if ((data.code === 200 || data.code === 0) && data.data) {
        const categories = data.data;
        const categoryNames = categories
          .filter((cat: any) => cat.name && cat.name.trim() !== '' && cat.name !== 'All')
          .map((cat: any) => cat.name);
        
        
        return categoryNames;
      }
      
      // Fallback to basic category endpoint (GET /category - 3 categories)
      try {
        const response = await fetch(`${this.config.baseUrl}${this.config.endpoints.categories}`, {
          method: 'GET',
          headers: this.config.authHeaders
        });

        const data = await response.json();
        
        if ((data.code === 200 || data.code === 0) && data.data) {
          const categories = data.data;
          const categoryNames = categories
            .filter((cat: any) => cat.name && cat.name.trim() !== '')
            .map((cat: any) => cat.name);
          
          
          if (categoryNames.length > 0) {
            return categoryNames;
          }
        }
      } catch (error) {
        
      }
      
      // Final fallback
      
      return [];
      
    } catch (error: any) {
      console.error('❌ SWS API getProductCategories error:', error);
      
      
      return [];
    }
  }

  // Helper method to build comprehensive product description from actual SWS API fields
  private buildProductDescription(item: any): string {
    const parts = [];
    
    // Add the product name first (this is the main searchable content)
    const productName = item.Name; // Use exact field name from API
    if (productName) {
      parts.push(productName);
    }
    
    // Add other actual fields from SWS API
    if (item.itemLabel) parts.push(item.itemLabel);
    if (item.note) parts.push(item.note);
    if (item.oemNo) parts.push(`OEM: ${item.oemNo}`);
    if (item.erpNo) parts.push(`ERP: ${item.erpNo}`);
    if (item.type) parts.push(`Type: ${item.type}`);
    if (item.brandName) parts.push(`Brand: ${item.brandName}`);
    if (item.categoryName) parts.push(`Category: ${item.categoryName}`);
    
    return parts.length > 0 ? parts.join(', ') : '';
  }

  // Helper methods to normalize SWS API responses to unified format
  private normalizeTagInfo(swsTagData: any): UnifiedTagInfo {
    // Log raw data for debugging
    console.log('🔍 SWS normalizeTagInfo raw data:', swsTagData);
    
    // Ensure productId is always a string for consistent comparison
    let productId: string | undefined;
    if (swsTagData.itemId !== undefined && swsTagData.itemId !== null) {
      productId = String(swsTagData.itemId);
    } else if (swsTagData.productId !== undefined && swsTagData.productId !== null) {
      productId = String(swsTagData.productId);
    }
    
    const normalized = {
      id: swsTagData.id?.toString() || swsTagData.rfid,
      rfidNumber: swsTagData.rfid,
      productId: productId,
      productName: swsTagData.itemName,
      productSku: swsTagData.sku,
      status: this.mapStatusFromSWS(swsTagData.status),
      location: swsTagData.location,
      quantity: swsTagData.quantity || swsTagData.packageQty || 1,
      notes: swsTagData.note
    };
    
    console.log('📦 SWS normalized tag info:', normalized);
    return normalized;
  }

  private normalizeProduct(swsProductData: any): UnifiedProduct {
    // Map SWS status: 0 = active, 1 = inactive
    const productStatus = swsProductData.status === 0 ? 'active' : 'inactive';
    
    // Only log status for debugging if needed (reduced logging)
    if (swsProductData.status !== 0 && swsProductData.status !== 1) {
      
    }
    
    return {
      id: swsProductData.id?.toString(),
      sku: swsProductData.sku,
      name: swsProductData.Name || swsProductData.itemName, // SWS uses capital "Name"
      description: swsProductData.Name, // Use Name as description too
      category: swsProductData.categoryName,
      manufacturer: swsProductData.manufactor, // Note: SWS uses "manufactor" not "manufacturer"
      image: swsProductData.image,
      price: parseFloat(swsProductData.price || '0'),
      unitCode: swsProductData.unitCode,
      status: productStatus
    };
  }

  private mapStatusToSWS(status: UnifiedTagInfo['status']): number {
    const statusMap = {
      'pending': 1,
      'not_matched': 1, // Map not_matched to pending for SWS compatibility
      'on_hand': 2,
      'consumed': 3,
      'removed': 4,
      'abandon': 8 // New abandon status
    };
    return statusMap[status] || 1;
  }

  private mapStatusFromSWS(swsStatus: any): UnifiedTagInfo['status'] {
    // Map SWS system status to unified status based on data analysis
    if (typeof swsStatus === 'number') {
      switch (swsStatus) {
        case 2: return 'pending';   // Newly created, not yet processed
        case 3: return 'on_hand';   // Active in warehouse with location
        case 4: return 'consumed';  // Used/consumed with complete log
        case 8: return 'abandon';   // Abandoned/replaced tag
        default: return 'pending';
      }
    }
    return 'pending';
  }

  // Helper method to enrich tags with accurate match dates


  // RFID Tag Management Methods for SWS
  async listRfidTags(options: { 
    page?: number; 
    pageSize?: number; 
    status?: string; 
    startDate?: string; 
    endDate?: string;
    getAllPages?: boolean;
    sortBy?: 'latest' | 'status';
    search?: string;
  } = {}): Promise<{ tags: UnifiedTagInfo[]; total: number; totalPages: number; currentPage: number }> {
    try {
      
      
      // Use warehouse endpoint directly - it has all the data we need
      if (options.search && options.search.trim()) {
        return this.searchRfidInWarehouse(options);
      } else {
        return this.listRfidTagsBasic(options);
      }
    } catch (error) {
      console.error('❌ Error fetching SWS RFID tags:', error);
      throw error;
    }
  }

  // Use warehouse endpoint directly
  private async listRfidTagsBasic(options: any): Promise<{ tags: UnifiedTagInfo[]; total: number; totalPages: number; currentPage: number }> {
    try {
      // Build query parameters using working combination from API testing
      const params = new URLSearchParams();
      params.append('whsId', '-1'); // -1 means "all warehouses"
      params.append('status', '-1'); // -1 means "all statuses" 
      params.append('isabnormal', '-1'); // -1 means "all abnormal statuses"
      params.append('itemsStatus', '-1'); // -1 means "all item statuses"
      params.append('total', '0'); // Standard pagination param
      
      // Add pagination parameters - smaller page size for enrichment
      params.append('page', (options.page || 1).toString());
      params.append('pageSize', (options.pageSize || 500).toString()); // Reasonable size without enrichment
      
      params.append('sku', '');
      params.append('name', '');
      params.append('rfid', '');
      
      const response = await fetch(
        `${this.config.baseUrl}${this.config.endpoints.rfidTags}?${params.toString()}`,
        {
          method: 'GET',
          headers: this.config.authHeaders
        }
      );
      
      const data = await response.json();

      if (data.code === 200 || data.code === 0) {
        const rfidTags = Array.isArray(data.data?.data) ? data.data.data : [];
        const totalCount = parseInt(data.data?.total?.replace(/,/g, '')) || rfidTags.length;
        
        const normalizedTags = rfidTags.map((tag: any) => this.normalizeRfidTag(tag));
        
        // Skip enrichment entirely for fast loading like the web apps
        // The warehouse endpoint already has timestamps (lastAt, updateAt, etc.)
        
        
        return {
          tags: normalizedTags,
          total: totalCount,
          totalPages: Math.ceil(totalCount / (options.pageSize || 50)),
          currentPage: options.page || 1
        };
      } else if (data.code === 50003) {
        
        throw new Error('Authentication required. Please log in again.');
      } else {
        
        return { tags: [], total: 0, totalPages: 0, currentPage: 1 };
      }
    } catch (error) {
      console.error('❌ Error in basic RFID listing:', error);
      throw error;
    }
  }

  async updateRfidTag(tagId: string, updates: Partial<UnifiedTagInfo>): Promise<void> {
    try {
      const endpoint = this.config.endpoints.updateTag;
      const requestBody = {
        tagId: parseInt(tagId),
        ...(updates.quantity && { packageQty: updates.quantity }),
        ...(updates.status && { status: this.mapStatusToSWS(updates.status) }),
        ...(updates.notes && { notes: updates.notes })
      };
      
      
      
      const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
        method: 'PUT',
        headers: this.config.authHeaders,
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      
      if (data.code !== 200) {
        throw new Error(data.message || 'Failed to update RFID tag');
      }
    } catch (error) {
      console.error('❌ SWS API updateRfidTag error:', error);
      throw new Error('Failed to update RFID tag');
    }
  }

  async deleteRfidTag(tagId: string, rfidNumber: string): Promise<void> {
    try {
      
      
      const endpoint = '/rfid/deleteTags';
      const requestBody = {
        method: "delete",
        rfid: [rfidNumber] // SWS expects array of RFID numbers
      };
      
      
      
      const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
        method: 'PUT',
        headers: this.config.authHeaders,
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      
      if (data.code === 200) {
        
      } else {
        throw new Error(data.message || `Failed to clear tag (code: ${data.code})`);
      }
    } catch (error) {
      console.error('❌ SWS API deleteRfidTag error:', error);
      throw new Error('Failed to clear tag: ' + (error instanceof Error ? error.message : String(error)));
    }
  }

  async clearTag(tagId: string, rfidNumber: string): Promise<void> {
    // For SWS, clearing a tag is the same as deleting it
    await this.deleteRfidTag(tagId, rfidNumber);
  }

  async assignRfidToProduct(tagId: string, productId: string): Promise<void> {
    try {
      // SWS doesn't have a separate assign endpoint - use update instead
      await this.updateRfidTag(tagId, { productId });
    } catch (error) {
      console.error('❌ SWS API assignRfidToProduct error:', error);
      throw new Error('Failed to assign RFID to product');
    }
  }

  async resetRfidTag(tagId: string, resetType: 'soft' | 'hard' = 'soft'): Promise<void> {
    try {
      // Reset by updating status to pending
      await this.updateRfidTag(tagId, { status: 'pending', notes: `Reset: ${resetType}` });
    } catch (error) {
      console.error('❌ SWS API resetRfidTag error:', error);
      throw new Error('Failed to reset RFID tag');
    }
  }

  async replaceRfidTag(oldRfid: string, newRfid: string): Promise<void> {
    try {
      // IMPORTANT: SWS requires form-urlencoded format, not JSON!
      // Use the SWS replaceItemRfid endpoint with type 2 for replacing tag
      const endpoint = '/replaceItemRfid';  // No /md prefix
      
      // Create form-urlencoded body
      const formData = new URLSearchParams();
      formData.append('rfid', oldRfid);
      formData.append('itemId', newRfid);  // For type 2, itemId is the new RFID tag
      formData.append('type', '2');  // Type 2 = replace tag
      
      console.log('🔄 SWS API replaceRfidTag form data:', formData.toString());
      
      // Update headers to use form-urlencoded content type
      // Keep the access-token and appId headers from config
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'access-token': this.config.authHeaders['access-token'],
        'appId': this.config.authHeaders['appId']
      };
      
      const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: headers,
        body: formData.toString()  // Send as URL-encoded string
      });

      const data = await response.json();
      
      console.log('📦 SWS API replaceRfidTag response:', data);
      
      if (data.code !== 200) {
        throw new Error(data.message || 'Failed to replace RFID tag');
      }
    } catch (error) {
      console.error('❌ SWS API replaceRfidTag error:', error);
      throw new Error('Failed to replace RFID tag: ' + (error instanceof Error ? error.message : String(error)));
    }
  }

  async changeTagProduct(rfid: string, newProductId: string): Promise<void> {
    /**
     * SWS CHANGE PRODUCT IMPLEMENTATION STATUS:
     * 
     * ENVIRONMENT-SPECIFIC BEHAVIOR (Verified 2025-08-21):
     * - PRODUCTION: ✅ Works correctly - Actually updates the product association
     * - TESTING/STAGING: ❌ Returns success (200) but does NOT update the backend
     * 
     * The UI has been disabled for SWS users via feature flag in EditTagModal.tsx
     * due to the testing environment issue.
     * 
     * Verified Test Case:
     * - Tag abc123 assigned to product ID 20956
     * - POST to /replaceItemRfid with type=1 to change to product ID 20951
     * - Production (api.reboundynamics.com): Successfully updated the association
     * - Testing (rhq-sws-staging.reboundynamics.com): Returned success but no actual update
     * 
     * Technical Details:
     * - Endpoint: POST /api/v1/replaceItemRfid with type=1
     * - Must use Content-Type: application/x-www-form-urlencoded (not JSON)
     * - Must use 'access-token' header (not Authorization: Bearer)
     * 
     * This code is preserved for when the testing environment is fixed.
     */
    try {
      // IMPORTANT: SWS requires form-urlencoded format, not JSON!
      // The newProductId passed in is already the product ID from the UI
      console.log('🔍 SWS API changeTagProduct - Input:', { rfid, newProductId });
      
      // Use the SWS replaceItemRfid endpoint with type 1 for changing product
      const endpoint = '/replaceItemRfid';  // No /md prefix
      
      // Create form-urlencoded body (like rfid=test&itemId=20951&type=1)
      const formData = new URLSearchParams();
      formData.append('rfid', rfid);
      formData.append('itemId', newProductId);  // Keep as string - backend expects string
      formData.append('type', '1');  // Type 1 = replace product
      
      console.log('🔄 SWS API changeTagProduct form data:', formData.toString());
      
      // Update headers to use form-urlencoded content type
      // Keep the access-token and appId headers from config
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'access-token': this.config.authHeaders['access-token'],
        'appId': this.config.authHeaders['appId']
      };
      
      const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: headers,
        body: formData.toString()  // Send as URL-encoded string
      });

      const data = await response.json();
      
      console.log('📦 SWS API changeTagProduct response:', data);
      
      // Check for success - SWS returns code 200 for success
      if (data.code === 200 || data.code === 0) {
        console.log('✅ SWS changeTagProduct successful');
        return; // Success!
      } else {
        console.error('❌ SWS changeTagProduct failed with code:', data.code, 'message:', data.message);
        throw new Error(data.message || `Failed to change product association (code: ${data.code})`);
      }
    } catch (error) {
      console.error('❌ SWS API changeTagProduct error:', error);
      throw new Error('Failed to change product association: ' + (error instanceof Error ? error.message : String(error)));
    }
  }

  // Helper method to normalize SWS RFID tag data to unified format
  private normalizeRfidTag(swsTagData: any): UnifiedTagInfo {
    // Convert SWS timestamp to ISO string if it's a Unix timestamp
    let matchedAtISO: string | null = null; // Default to null, will use a placeholder date if no real date found
    
    // Try different date fields in order of preference
    if (swsTagData.InDate && swsTagData.InDate > 0) {
      // InDate appears to be Unix timestamp
      matchedAtISO = new Date(swsTagData.InDate * 1000).toISOString();
    } else if (swsTagData.lastAt && swsTagData.lastAt > 0) {
      // lastAt appears to be Unix timestamp
      matchedAtISO = new Date(swsTagData.lastAt * 1000).toISOString();
    } else if (swsTagData.updateAt && swsTagData.updateAt > 0) {
      // updateAt appears to be Unix timestamp  
      matchedAtISO = new Date(swsTagData.updateAt * 1000).toISOString();
    } else if (swsTagData.abnormalAt && swsTagData.abnormalAt > 0) {
      // abnormalAt appears to be Unix timestamp
      matchedAtISO = new Date(swsTagData.abnormalAt * 1000).toISOString();
    } else if (swsTagData.consumeDate && swsTagData.consumeDate > 0) {
      // consumeDate appears to be Unix timestamp
      matchedAtISO = new Date(swsTagData.consumeDate * 1000).toISOString();
    } else if (swsTagData.updateDate) {
      // Try formatted date string
      const dateStr = `${swsTagData.updateDate} ${swsTagData.updateTime || ''}`.trim();
      const parsedDate = new Date(dateStr);
      if (!isNaN(parsedDate.getTime())) {
        matchedAtISO = parsedDate.toISOString();
      }
    } else if (swsTagData.InData) {
      // Try formatted date string
      const dateStr = `${swsTagData.InData} ${swsTagData.InTime || ''}`.trim();
      const parsedDate = new Date(dateStr);
      if (!isNaN(parsedDate.getTime())) {
        matchedAtISO = parsedDate.toISOString();
      }
    }
    
    // If no valid date found, use a default historical date instead of current time
    // This makes it clear these are older tags without specific match dates
    if (!matchedAtISO) {
      matchedAtISO = new Date('2023-01-01T00:00:00.000Z').toISOString(); // Default to a past date
    }
    
    // Debug: Log available ID fields from SWS warehouse data
    const dbId = swsTagData.itemId?.toString() || swsTagData.id?.toString();
    const rfidId = swsTagData.rfid || swsTagData.rfidNumber;
    
     // Log just the keys to reduce noise
    
    return {
      id: dbId || rfidId, // Use database ID first, fallback to RFID
      rfidNumber: rfidId,
      productId: swsTagData.itemId?.toString() || swsTagData.productId?.toString(),
      productName: swsTagData.Name || swsTagData.itemName || swsTagData.productName, // SWS uses capital "Name"
      productSku: swsTagData.sku || swsTagData.productSku,
      status: this.mapStatusFromSWS(swsTagData.status),
      location: swsTagData.whsName || swsTagData.nwhsName || swsTagData.location, // SWS uses whsName for warehouse name
      smartAreaLocation: swsTagData.nwhsCode || '', // Warehouse code as secondary location
      quantity: parseFloat(swsTagData.packageQty) || swsTagData.quantity || 1,
      notes: swsTagData.notes || swsTagData.note,
      matchedBy: swsTagData.createdBy || 'system',
      matchedAt: matchedAtISO
    };
  }

  // Search for RFID in warehouse endpoint (better for exact searches)
  private async searchRfidInWarehouse(options: any): Promise<{ tags: UnifiedTagInfo[]; total: number; totalPages: number; currentPage: number }> {
    try {
      
      
      // First, try warehouse endpoint to get basic info
      const params = new URLSearchParams();
      params.append('whsId', '-1'); // -1 means "all warehouses"
      params.append('status', '-1'); // -1 means "all statuses" 
      params.append('isabnormal', '-1'); // -1 means "all abnormal statuses"
      params.append('itemsStatus', '-1'); // -1 means "all item statuses"
      params.append('total', '0'); // Standard pagination param
      params.append('page', (options.page || 1).toString());
      params.append('pageSize', (options.pageSize || 100).toString());
      params.append('sku', '');
      params.append('name', '');
      params.append('rfid', options.search || ''); // Pass the exact RFID to search for
      
      
      
      const response = await fetch(
        `${this.config.baseUrl}${this.config.endpoints.rfidTags}?${params.toString()}`,
        {
          method: 'GET',
          headers: this.config.authHeaders
        }
      );
      
      const data = await response.json();
      
      
      if ((data.code === 200 || data.code === 0) && data.data?.data) {
        // Use warehouse data directly - it has all the information we need
        const warehouseTags = data.data.data;
        
        
        // Warehouse data already contains all the fields we need:
        // - itemId: database ID
        // - rfid: RFID number
        // - sku: product SKU  
        // - Name: product name
        // - status: tag status
        // - packageQty: package quantity
        
        const totalCount = parseInt(data.data?.total?.replace(/,/g, '')) || warehouseTags.length;
        
        
        
        // Convert to unified format using warehouse data
        const unifiedTags = warehouseTags.map((tag: any) => this.normalizeRfidTag(tag));
        
        return {
          tags: unifiedTags,
          total: totalCount,
          totalPages: Math.ceil(totalCount / (options.pageSize || 100)),
          currentPage: options.page || 1
        };
      } else {
        
        return {
          tags: [],
          total: 0,
          totalPages: 0,
          currentPage: 1
        };
      }
    } catch (error) {
      console.error('? Error searching warehouse:', error);
      throw error;
    }
  }


  loadLocationDataForTags(rfids: string[]): Promise<Map<string, any>> {
    // SWS warehouse endpoint already includes location data (whsName field)
    // No need for separate location loading
    return Promise.resolve(new Map());
  }

  getCurrentSystem(): 'smart' | 'sws' {
    return 'sws';
  }

  getCurrentUser(): User | null {
    return this.user;
  }

  /**
   * Check if connected to production environment
   * Production URL: api.reboundynamics.com
   * Testing/Staging URL: rhq-sws-staging.reboundynamics.com
   */
  isProductionEnvironment(): boolean {
    // Get the actual backend URL from config, not the proxy URL
    const config = configService.getConfig();
    const actualBackendUrl = config.swsApi.baseUrl;
    
    // Check if the actual backend URL contains the production domain
    return actualBackendUrl.includes('api.reboundynamics.com');
  }
}
